import React from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@mantine/core';
import classNames from 'classnames';
import useHardwareFirstTrialTerms from '../../hooks/useHardwareFirstTrialTerms';
import * as types from '../../types';
import HardwareFirstTrialModal from './HardwareFirstTrialModal';
import styles from './HardwareFirstTrialToggle.module.scss';

interface HardwareFirstTrialToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  productCategory: types.ProductCategory;
  /** the tracker's list price, charged 30 days after setup; the terms link waits for it */
  listPriceInCents: number | undefined;
}

// the prototype's 42x26 switch with a 20px knob; mantine has no preset at that size
const switchVars = () => ({
  root: {
    '--switch-width': '42px',
    '--switch-height': '26px',
    '--switch-thumb-size': '20px',
    '--switch-track-label-padding': '3px',
  },
});

/**
 * The "try it free for 30 days" card for a hardware-first tracker: the offer and its terms link on the
 * left, the switch on the right. The caller owns the state and renders it only for an eligible shopper.
 */
export default function HardwareFirstTrialToggle({
  checked,
  onChange,
  productCategory,
  listPriceInCents,
}: HardwareFirstTrialToggleProps) {
  const { t } = useTranslation();
  const terms = useHardwareFirstTrialTerms(listPriceInCents);
  const title = t('hardware_first_trial_toggle_title');

  // the whole card flips the trial; the switch and the terms modal stop their clicks so they do not flip it twice
  const stop = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <div className={classNames(styles.container, { [styles.on]: checked })} onClick={() => onChange(!checked)}>
      <div className={styles.text}>
        <span className={styles.title}>{title}</span>
        <span className={styles.sub}>
          {t('hardware_first_trial_toggle_sub')}
          {terms && (
            <span onClick={stop}>
              {' '}
              <HardwareFirstTrialModal
                productCategory={productCategory}
                source="shop"
                terms={terms}
                trigger={
                  <button className={styles.learnMore} type="button">
                    {t('learn_more')}
                  </button>
                }
              />
            </span>
          )}
        </span>
      </div>
      <span onClick={stop}>
        <Switch
          aria-label={title}
          checked={checked}
          onChange={(event) => onChange(event.currentTarget.checked)}
          color="black"
          vars={switchVars}
          withThumbIndicator={false}
          classNames={{ track: styles.switchTrack, thumb: styles.switchThumb }}
        />
      </span>
    </div>
  );
}
